import { useEffect, useState } from 'react';

// material-ui
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';

// project imports
import EarningCard from './EarningCard';
import TotalIncomeDarkCard from 'ui-component/cards/TotalIncomeDarkCard';
import TotalIncomeLightCard from 'ui-component/cards/TotalIncomeLightCard';
import CreditChartCard from './CreditLimit';
import chartData from './chart-data';
import useConfig from 'hooks/useConfig';
import ApexCharts from 'apexcharts';

// constants and assets
import { gridSpacing } from 'store/constant';
import StorefrontTwoToneIcon from '@mui/icons-material/StorefrontTwoTone';
import PoliciesCard from './PoliciesCard';
import { getAgencyNameFromStorage, getUserRegionFromStorage, getUserRoleNameFromStorage } from 'Application/utils';
import Policies from './RecentPolicies';
import { useBritamStore } from 'Application/store/useBritamStatsStore';
import QuotesCard from './QuotesCard';
import { Box } from '@mui/system';
import { BodyText } from 'Application/components';
import MainCard from 'ui-component/cards/MainCard';

// ==============================|| DEFAULT DASHBOARD ||============================== //
import Kenya from 'assets/images/kenya.png';
import Rwanda from 'assets/images/rwanda.png';
import userCountryStore from 'Application/store/userCountryStore';
import { Autocomplete, InputAdornment, TextField } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public'; // Import world icon

const countries = [
    { label: 'Kenya', id: 1, flag: Kenya },
    { label: 'Rwanda', id: 2, flag: Rwanda }
];
const AdminDashboard = () => {
    const { selectedAdminCountry, setSelectedAdminCountry } = userCountryStore();
    console.log(selectedAdminCountry?.label, 'selectedAdminCountry');

    const { getBritamStats, britamStatsData, isLoading } = useBritamStore(); // Access the store's data
    const getCurrencyByRegion = (region: string | undefined) => {
        if (!region) return 'Unknown Currency'; // Default case

        const currencyMap: Record<string, string> = {
            Kenya: 'KES',
            Rwanda: 'RWF'
        };

        return currencyMap[region] || 'Unknown Currency'; // Default if region is not listed
    };

    const regionName = selectedAdminCountry?.label || getUserRegionFromStorage();
    const currency = getCurrencyByRegion(regionName);

    useEffect(() => {
        getBritamStats(regionName);
    }, [regionName, getBritamStats]);

    const userRole = getUserRoleNameFromStorage();

    const theme = useTheme();
    const { mode } = useConfig();

    const backColor = theme.palette.background.paper;
    const secondary = theme.palette.secondary.main;
    const error = theme.palette.error.main;
    const primary = theme.palette.primary.main;

    // Updating the chart options on theme mode or color changes
    useEffect(() => {
        const newRevenueChartCardData = {
            ...chartData.RevenueChartCardData,
            colors: [error, primary, secondary],
            stroke: { colors: [backColor] }
        };

        // Make sure ApexCharts is imported before using exec
        if (typeof ApexCharts !== 'undefined') {
            ApexCharts.exec('revenue-chart', 'updateOptions', newRevenueChartCardData);
        }
    }, [mode, backColor, secondary, error, primary]);

    return (
        <>
            {userRole === 'ROLE_REGIONAL_ADMIN' && (
                <MainCard sx={{ marginTop: '20px', marginBottom: '20px' }}>
                    <Box>
                        <BodyText
                            text={
                                'Here are the key statistics generated from various activities, including user management, agency operations, and policy handling.'
                            }
                        />
                    </Box>
                </MainCard>
            )}

            {userRole === 'ROLE_ADMINISTRATOR' && (
                <MainCard sx={{ marginTop: '20px', marginBottom: '20px' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2
                        }}
                    >
                        <BodyText
                            text={
                                'Monitor key statistics from various activities, including user management, agency operations, and policy handling across all regions.'
                            }
                        />
                        <Autocomplete
                            disableClearable
                            options={countries}
                            value={selectedAdminCountry || null}
                            onChange={(_, newValue) => setSelectedAdminCountry(newValue)}
                            sx={{
                                width: 200,
                                '& .MuiOutlinedInput-root': {
                                    borderColor: '#0369a1',
                                    '&:hover fieldset': { borderColor: '#0369a1' },
                                    '&.Mui-focused fieldset': { borderColor: '#0369a1' }
                                }
                            }}
                            getOptionLabel={(option) => option.label}
                            renderOption={(props, option) => (
                                <li
                                    {...props}
                                    key={option.id}
                                    className="flex items-center gap-2 px-3 py-2 rounded-md transition-all duration-200 cursor-pointer"
                                    style={{ backgroundColor: 'white' }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#E0F2FE')}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                                >
                                    <img src={option.flag} alt={option.label} className="w-5 h-5" />
                                    {option.label}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Choose a country"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PublicIcon sx={{ color: '#0369a1' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            )}
                        />
                    </Box>
                </MainCard>
            )}

            <Grid container spacing={gridSpacing}>
                {/* First Row */}
                <Grid item xs={12}>
                    <Grid container spacing={gridSpacing}>
                        <Grid item lg={4} md={6} sm={6} xs={12}>
                            <EarningCard isLoading={isLoading} amountOfPolicies={britamStatsData?.totalPremiums} currency={currency} />
                        </Grid>
                        <Grid item lg={4} md={6} sm={6} xs={12}>
                            <PoliciesCard isLoading={isLoading} amountOfPolicies={britamStatsData?.numOfPolicies} />
                        </Grid>
                        <Grid item lg={4} md={6} sm={6} xs={12}>
                            <QuotesCard isLoading={isLoading} amountOfPolicies={britamStatsData?.totalCredit} currency={currency} />
                        </Grid>
                        <Grid item lg={4} md={12} sm={12} xs={12}>
                            <Grid container spacing={gridSpacing}>
                                <Grid item sm={6} xs={12} md={6} lg={12}>
                                    <TotalIncomeLightCard
                                        isLoading={isLoading}
                                        total={britamStatsData?.numOfAgencies}
                                        label="Number Of Agencies"
                                        icon={<StorefrontTwoToneIcon fontSize="inherit" />}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item lg={4} md={12} sm={12} xs={12}>
                            <Grid container spacing={gridSpacing}>
                                <Grid item sm={6} xs={12} md={6} lg={12}>
                                    <TotalIncomeLightCard
                                        isLoading={isLoading}
                                        total={britamStatsData?.numOfUsers}
                                        label="Number Of Users"
                                        icon={<StorefrontTwoToneIcon fontSize="inherit" />}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item lg={4} md={12} sm={12} xs={12}>
                            <Grid container spacing={gridSpacing}>
                                <Grid item sm={6} xs={12} md={6} lg={12}>
                                    <TotalIncomeLightCard
                                        isLoading={isLoading}
                                        total={britamStatsData?.numOfQuotes}
                                        label="Total Quotes"
                                        icon={<StorefrontTwoToneIcon fontSize="inherit" />}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default AdminDashboard;
